import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AppData } from '../helpers/app-data';

export interface DashboardMetrics {
  TotalProcesses: number;
  ActiveTasks: number;
  CompletedProcesses: number;
  CancelledProcesses: number;
  PendingTasks: number;
  ActiveUsers: number;
  ExecutionProcesses: number;
  AverageExecutionTime: number;
}

export interface ProcessTimeline {
  Month: string;
  ExecutionTime: number;
  ProcessCount: number;
  Date: Date;
}

export interface ProcessStatus {
  Status: string;
  Count: number;
  Percentage: number;
}

export interface UserActivity {
  Month: string;
  ActiveUsers: number;
  Date: Date;
  TotalTasks: number;
  CompletedTasks: number;
}

export interface ProcessSpecificMetrics {
  ProcessExecutionId: number;
  ProcessName: string;
  ProcessStatus: string;
  ExecutionTimeHours: number;
  TotalTasks: number;
  CompletedTasks: number;
  PendingTasks: number;
  FailedTasks: number;
  ProgressPercentage: number;
  StartDate: string;
  EndDate?: string;
  CreatedBy: string;
  ProcessVersion: string;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private metricsSubject = new BehaviorSubject<DashboardMetrics | null>(null);
  public metrics$ = this.metricsSubject.asObservable();

  constructor(private http: HttpClient) {}

  // Obtener métricas principales del dashboard
  getDashboardMetrics(): Observable<DashboardMetrics> {
    const apiUrl = AppData.API_URL;
    
    return this.http.get<any>(`${apiUrl}Dashboard/GetMetrics`).pipe(
      map(response => {
        // Verificar si la respuesta es exitosa
        if (response && response.Ok === true && response.Data) {
          this.metricsSubject.next(response.Data);
          return response.Data;
        }
        
        // Si hay un error en la respuesta del backend
        if (response && response.Ok === false) {
          console.error('Error del servidor:', response.Error);
          throw new Error(response.Error || 'Error del servidor al obtener métricas del dashboard');
        }
        
        // Si la estructura de respuesta es inesperada
        throw new Error('Respuesta del servidor con formato inesperado');
      }),
      catchError(error => {
        console.error('Error fetching dashboard metrics:', error);
        console.error('Detalles del error:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message
        });
        
        // En caso de error, retornar datos mock o valores por defecto
        const defaultMetrics: DashboardMetrics = {
          TotalProcesses: 0,
          ActiveTasks: 0,
          CompletedProcesses: 0,
          CancelledProcesses: 0,
          PendingTasks: 0,
          ActiveUsers: 0,
          ExecutionProcesses: 0,
          AverageExecutionTime: 0
        };
        
        this.metricsSubject.next(defaultMetrics);
        return of(defaultMetrics);
      })
    );
  }

  // Obtener datos de línea de tiempo de procesos
  getProcessTimeline(): Observable<ProcessTimeline[]> {
    const apiUrl = AppData.API_URL;
    
    return this.http.get<any>(`${apiUrl}Dashboard/GetProcessTimeline`).pipe(
      map(response => {
        // Verificar si la respuesta es exitosa
        if (response && response.Ok === true && response.Data) {
          return response.Data;
        }
        
        // Si hay un error en la respuesta del backend
        if (response && response.Ok === false) {
          console.error('Error del servidor:', response.Error);
          throw new Error(response.Error || 'Error del servidor al obtener timeline de procesos');
        }
        
        // Si la estructura de respuesta es inesperada
        throw new Error('Respuesta del servidor con formato inesperado');
      }),
      catchError(error => {
        console.error('Error fetching process timeline:', error);
        console.error('Detalles del error:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message
        });
        
        // Retornar array vacío en lugar de datos mock
        return of([]);
      })
    );
  }

  // Obtener estado de procesos
  getProcessStatus(): Observable<ProcessStatus[]> {
    const apiUrl = AppData.API_URL;
    
    return this.http.get<any>(`${apiUrl}Dashboard/GetProcessStatus`).pipe(
      map(response => {
        // Verificar si la respuesta es exitosa
        if (response && response.Ok === true && response.Data) {
          return response.Data;
        }
        
        // Si hay un error en la respuesta del backend
        if (response && response.Ok === false) {
          console.error('Error del servidor:', response.Error);
          throw new Error(response.Error || 'Error del servidor al obtener estado de procesos');
        }
        
        // Si la estructura de respuesta es inesperada
        throw new Error('Respuesta del servidor con formato inesperado');
      }),
      catchError(error => {
        console.error('Error fetching process status:', error);
        console.error('Detalles del error:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message
        });
        
        // Retornar array vacío en lugar de datos mock
        return of([]);
      })
    );
  }

  // Obtener datos de actividad de usuarios
  getUserActivity(): Observable<UserActivity[]> {
    const apiUrl = AppData.API_URL;
    
    return this.http.get<any>(`${apiUrl}Dashboard/GetUserActivity`).pipe(
      map(response => {
        // Verificar si la respuesta es exitosa
        if (response && response.Ok === true && response.Data) {
          return response.Data;
        }
        
        // Si hay un error en la respuesta del backend
        if (response && response.Ok === false) {
          console.error('Error del servidor:', response.Error);
          throw new Error(response.Error || 'Error del servidor al obtener actividad de usuarios');
        }
        
        // Si la estructura de respuesta es inesperada
        throw new Error('Respuesta del servidor con formato inesperado');
      }),
      catchError(error => {
        console.error('Error fetching user activity:', error);
        console.error('Detalles del error:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message
        });
        
        // Retornar array vacío en lugar de datos mock
        return of([]);
      })
    );
  }

  // Obtener métricas específicas de un proceso
  getProcessSpecificMetrics(processExecutionId: number): Observable<ProcessSpecificMetrics> {
    const apiUrl = AppData.API_URL;
    
    return this.http.get<any>(`${apiUrl}Dashboard/GetProcessSpecificMetrics?processExecutionId=${processExecutionId}`).pipe(
      map(response => {
        // Verificar si la respuesta es exitosa
        if (response && response.Ok === true && response.Data) {
          return response.Data;
        }
        
        // Si hay un error en la respuesta del backend
        if (response && response.Ok === false) {
          console.error('Error del servidor:', response.Error);
          throw new Error(response.Error || 'Error del servidor al obtener métricas específicas del proceso');
        }
        
        // Si la estructura de respuesta es inesperada
        throw new Error('Respuesta del servidor con formato inesperado');
      }),
      catchError(error => {
        console.error('Error fetching process specific metrics:', error);
        console.error('Detalles del error:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message
        });
        
        // En caso de error, retornar datos por defecto
        const defaultMetrics: ProcessSpecificMetrics = {
          ProcessExecutionId: processExecutionId,
          ProcessName: 'Proceso desconocido',
          ProcessStatus: 'ERROR',
          ExecutionTimeHours: 0,
          TotalTasks: 0,
          CompletedTasks: 0,
          PendingTasks: 0,
          FailedTasks: 0,
          ProgressPercentage: 0,
          StartDate: new Date().toISOString(),
          EndDate: undefined,
          CreatedBy: 'Usuario desconocido',
          ProcessVersion: '1.0'
        };
        
        return of(defaultMetrics);
      })
    );
  }
}
